import styled from 'styled-components';
import { Box, FlexBox, Stack, StackProps } from '@ardoq/layout';
import { Header3, InlineText, Paragraph } from '@ardoq/typography';
import { Children, isValidElement, ReactNode, useRef } from 'react';
import { ErrorStateNotFoundIllustration } from './ErrorStateIllustrations';
import {
  ArdoqError,
  getArdoqErrorMessage,
  getArdoqErrorTraceId,
} from '@ardoq/common-helpers';
import { CopyToClipboard } from '@ardoq/button';

export type ErrorStateProps = {
  title?: string;
  error: ArdoqError;
  children?: ReactNode;
  size?: keyof typeof ErrorStateMediaSize;
  layout?: 'horizontal' | 'vertical';
};

export const ErrorStateMediaSize = {
  small: '110px',
  large: '175px',
};

const MediaContainer = styled.div<{ $size: ErrorStateProps['size'] }>`
  width: ${({ $size }) =>
    $size === 'small' ? ErrorStateMediaSize.small : ErrorStateMediaSize.large};
`;

export type ErrorStateActionsProps = {
  children: ReactNode;
};

export const ErrorStateActions = (_: ErrorStateActionsProps) => {
  useRef(() => {
    throw new Error('ErrorStateActions needs to be inside <ErrorState/>');
  });
  return <></>;
};

export const PrivateErrorStateActions = ({
  children,
  align,
}: ErrorStateActionsProps & Pick<StackProps, 'align'>) => (
  <Stack gap="medium" align={align}>
    {children}
  </Stack>
);

export const ErrorState = ({
  title = 'We’re having trouble on our end.',
  error,
  layout = 'horizontal',
  size = 'large',
  children,
}: ErrorStateProps) => {
  const LayoutContent = layout === 'horizontal' ? FlexBox : Stack;
  const message = getArdoqErrorMessage(error);
  const traceId = getArdoqErrorTraceId(error);

  return (
    <FlexBox justify={layout === 'horizontal' ? 'start' : 'center'}>
      <Box maxWidth="500px">
        <LayoutContent gap="medium" align="center">
          <Box>
            <MediaContainer $size={size}>
              <ErrorStateNotFoundIllustration />
            </MediaContainer>
          </Box>
          <Stack
            gap="medium"
            align={layout === 'vertical' ? 'center' : 'start'}
          >
            <Stack
              gap="small"
              align={layout === 'vertical' ? 'center' : 'start'}
            >
              <Header3 align={layout === 'vertical' ? 'center' : 'left'}>
                {title}
              </Header3>
              <Paragraph
                align={layout === 'vertical' ? 'center' : 'left'}
                variant="text2"
              >
                {message}
              </Paragraph>
              {traceId && (
                <Box marginTop="small">
                  <FlexBox gap="small">
                    <Paragraph>
                      <InlineText variant="text2Bold">Trace ID</InlineText>
                      <FlexBox gap="small">
                        <Paragraph>{traceId}</Paragraph>
                        <CopyToClipboard
                          tooltipText="Copy Trace ID"
                          copyText={traceId}
                        />
                      </FlexBox>
                    </Paragraph>
                  </FlexBox>
                </Box>
              )}
            </Stack>
            {Children.toArray(children).map(child => {
              if (
                typeof child === 'object' &&
                isValidElement(child) &&
                child.type === ErrorStateActions
              ) {
                return (
                  <PrivateErrorStateActions
                    key={child.key}
                    align={layout === 'vertical' ? 'center' : 'start'}
                    {...(child.props as ErrorStateActionsProps)}
                  />
                );
              }
              return child;
            })}
          </Stack>
        </LayoutContent>
      </Box>
    </FlexBox>
  );
};
